import React, { useEffect, useState } from 'react';
import { REGISTRY_TARGET, type MarkOutcome, type MarkPair, type MarkResult } from '../../../../api/migrationRunner';
import type { RunnerNav } from '../nav';
import { Btn, Choice, Dialog, ErrorBox, shortName } from '../ui';

/** What every Clean up screen gets from the tab. */
export interface CleanupCtx {
  nav: RunnerNav;
  isOwner: boolean;
  /** The Clean up registry migration is applied (marks, scans, ignores work). */
  ready: boolean;
  notify: (text: string, undo?: () => Promise<void> | void) => void;
  /** Mark pairs, toast with Undo. Returns null when it failed (error shown by the caller). */
  mark: (items: MarkPair[], outcome: MarkOutcome, note: string, done: (r: MarkResult) => string) => Promise<MarkResult>;
  go: (screen: Screen) => void;
  /** Something changed: reload counts (badge, cards). */
  changed: () => void;
}

export type Screen = 'overview' | 'template' | 'pending' | 'stuck' | 'fresh';

/** "Oct 18" (operator's local day). */
export const today = () => new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export const dbLabel = (db: string, names?: Record<string, string>) =>
  db === REGISTRY_TARGET ? 'Registry' : names?.[db] || db.replace(/^cma_/, '');

/** "migrations-2026-10-16-customer-dwell/001-x.sql" → "2026-10-16-customer-dwell / 001-x.sql" */
export const migName = (folderPath: string) => shortName(folderPath);

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export const SKIP_REASON: Record<string, string> = {
  already_done: 'already applied or resolved',
  running: 'still running',
  unknown_migration: 'migration gone',
  unknown_target: 'company gone',
  scope_mismatch: 'wrong scope',
  not_found: 'run not found',
  not_a_migration: 'ad-hoc SQL',
  changed: 'changed meanwhile',
};

export function skippedText(r: MarkResult) {
  if (!r.skipped.length) return '';
  const by = new Map<string, number>();
  for (const s of r.skipped) by.set(s.reason, (by.get(s.reason) || 0) + 1);
  return ` Skipped ${[...by].map(([k, n]) => `${n} ${SKIP_REASON[k] || k}`).join(', ')}.`;
}

/** A big clickable overview card (the mockup's .big). */
export const BigCard: React.FC<{
  icon: React.ReactNode; count: React.ReactNode; title: string; body: React.ReactNode; tone: string; label: string; onOpen: () => void;
}> = ({ icon, count, title, body, tone, label, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-blue-500 focus-visible:outline-2 focus-visible:outline-blue-600"
  >
    <span className="flex items-center justify-between">
      <span className="text-slate-400">{icon}</span>
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold ${tone}`}>{label}</span>
    </span>
    <span className="text-3xl font-extrabold leading-none tabular-nums text-slate-900">{count}</span>
    <b className="text-slate-900">{title}</b>
    <span className="text-[12.5px] text-slate-500">{body}</span>
    <span className="text-[13px] font-bold text-blue-600">Open &rarr;</span>
  </button>
);

export const TONE_CLS = {
  ok: 'bg-green-100 text-green-700',
  warn: 'bg-amber-100 text-amber-800',
  bad: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',
  mut: 'bg-slate-100 text-slate-500',
};

/** A pair list inside a dialog. */
export const PairList: React.FC<{ items: { key: string; folderPath: string; companyName: string }[] }> = ({ items }) => (
  <div className="max-h-44 overflow-auto rounded-lg border border-slate-200">
    <ul className="divide-y divide-slate-100 text-[12.5px]">
      {items.slice(0, 300).map((x) => (
        <li key={x.key} className="px-3 py-1.5"><b className="font-mono text-[11.5px]">{migName(x.folderPath)}</b> <span className="text-slate-500">on {x.companyName}</span></li>
      ))}
      {items.length > 300 && <li className="px-3 py-1.5 text-slate-500">and {items.length - 300} more</li>}
    </ul>
  </div>
);

/**
 * Confirm a bulk mark: the pairs, the note (filled in, editable), and for
 * mixed groups a choice of outcome.
 */
export const BulkMarkDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  sub: string;
  items: { key: string; folderPath: string; companyName: string }[];
  note: string;
  cta: string;
  choose?: boolean;
  outcome: MarkOutcome;
  onConfirm: (note: string, outcome: MarkOutcome) => Promise<void>;
}> = ({ open, onClose, title, sub, items, note: initial, cta, choose = false, outcome: initialOutcome, onConfirm }) => {
  const [note, setNote] = useState(initial);
  const [outcome, setOutcome] = useState<MarkOutcome>(initialOutcome);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setNote(initial); setOutcome(initialOutcome); setError(null); } }, [open, initial, initialOutcome]);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(note.trim(), outcome);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={<><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn kind="success" onClick={submit} disabled={busy || note.trim().length < 5}>{busy ? 'Saving…' : cta}</Btn></>}
    >
      <p className="text-slate-500">{sub}</p>
      <PairList items={items} />
      {choose && (
        <div className="space-y-2">
          <Choice name="bulk-outcome" checked={outcome === 'applied_by_hand'} onChange={() => setOutcome('applied_by_hand')} title="It's done (I applied it some other way)" />
          <Choice name="bulk-outcome" checked={outcome === 'not_needed'} onChange={() => setOutcome('not_needed')} title="Not needed for these companies" />
        </div>
      )}
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Note (saved with each one)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
      </label>
      <p className="text-xs text-slate-500">Recorded as resolved with your name, never as a real success. Nothing runs on any database. Undo from History for 30 days.</p>
      {error && <ErrorBox>{error}</ErrorBox>}
    </Dialog>
  );
};

export type ResolvePick = 'done' | 'not_needed' | 'later';

/** Resolve one pair from any state (mockup "Resolve …": done / not needed / later). */
export const ResolveDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  state: string;
  allowLater?: boolean;
  initial?: ResolvePick;
  onConfirm: (pick: ResolvePick, note: string) => Promise<void>;
}> = ({ open, onClose, title, state, allowLater = false, initial = 'done', onConfirm }) => {
  const [pick, setPick] = useState<ResolvePick>(initial);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setPick(initial); setNote(''); setError(null); } }, [open, initial]);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(pick, note.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const needsNote = pick !== 'later';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={<><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={submit} disabled={busy || (needsNote && note.length < 5)}>{busy ? 'Saving…' : 'Save'}</Btn></>}
    >
      <p className="text-slate-500">Currently: {state}. This only records a decision; nothing runs.</p>
      <Choice name="resolve-pick" checked={pick === 'done'} onChange={() => setPick('done')} title="I finished it by hand">
        The rest of the file is in. Marks it done (resolved, your name).
      </Choice>
      <Choice name="resolve-pick" checked={pick === 'not_needed'} onChange={() => setPick('not_needed')} title="Not needed for this company">
        It stops showing as pending here.
      </Choice>
      {allowLater && (
        <Choice name="resolve-pick" checked={pick === 'later'} onChange={() => setPick('later')} title="Leave it, check again later">
          Clears "stuck" (marks it stopped) so it can be resumed or re-run.
        </Choice>
      )}
      {needsNote && (
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Note</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What did you do? (at least 5 characters)" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
        </label>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
    </Dialog>
  );
};
